import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { ENV_CONFIG } from 'src/app/core/injection.token';
import { NotificationService } from 'src/app/core/services/notification.service';
import { ImageCaptureModalComponent } from 'src/app/extensions/components/image-capture-modal/image-capture-modal.component';
import {
  DOCUMENT_CATEGORY_SECTION,
  FILE_EXTENSION,
  FILE_UPLOAD_ERROR_CODE,
  USER_ROLES,
} from 'src/app/extensions/constants/commonConstants';
import { FileEvents, RequestDocumentsEvents } from 'src/app/extensions/services/api/events';
import { FileStoreAPIService } from 'src/app/extensions/services/api/file-store-api.service';
import { RequestAPIService } from 'src/app/extensions/services/api/request-api.service';
import { GlobalService } from 'src/app/extensions/services/global.service';
import { isNotEmpty } from 'src/app/extensions/utilities';

@Component({
  selector: 'request-documents',
  templateUrl: './request-documents.component.html',
  styleUrl: './request-documents.component.scss',
  standalone: false,
})
export class RequestDocumentsComponent implements OnInit, OnDestroy {
  @Input() selectedCategory: string;
  @Input() requestId: string;
  @Input() requestTypeCode: string;
  toUploadDocuments: app.models.RequestXDocument[] = [];

  DOCUMENT_CATEGORY_SECTION = DOCUMENT_CATEGORY_SECTION;

  UPLOAD_ALLOWED_EXTENSIONS = [
    ...this._envConfig.ALLOWED_FILE_EXTENSIONS.DOCUMENTS,
    ...this._envConfig.ALLOWED_FILE_EXTENSIONS.IMAGES,
  ].map(extension => extension.toLowerCase());

  acceptExtensions = this.UPLOAD_ALLOWED_EXTENSIONS.map(extension => `.${extension}`).join(',');

  documents: app.models.RequestXDocument[] = [];
  markDocumentsRead: Subject<string[]> = new Subject<[]>();
  subscriptions: Subscription[] = [];
  public isAdmin: boolean = false;

  constructor(
    private _fileStoreAPIService: FileStoreAPIService,
    private _notificationService: NotificationService,
    private _translateService: TranslateService,
    private _requestAPIService: RequestAPIService,
    private _globalService: GlobalService,
    private _ngbModal: NgbModal,
    @Inject(ENV_CONFIG) private _envConfig: env.EnvConfigurationData
  ) {}

  ngOnInit(): void {
    this.isAdmin = this._globalService.isAdminUser();

    this._requestAPIService.getRequestDocuments(this.requestId, this.requestTypeCode).subscribe(x => {
      this.documents = x.data;
    });

    let subscription = this.markDocumentsRead?.subscribe(documentIds => {
      if (documentIds.some(x => x)) {
        this._requestAPIService.markDocumentsDownloadDate(documentIds, this.requestTypeCode).subscribe(response => {
          if (response.isSuccess) {
            const objEvent = response.response.events.find(
              x => x.eventType == RequestDocumentsEvents.DocumentsDownloadDateMarked
            );

            this.documents
              .filter(x => documentIds.includes(x.documentId))
              .forEach(document => {
                document.userFirstDownloadDate = objEvent.eventBody.requestXUserFirstDownloadDate[document.documentId];
              });
          }
        });
      }
    });
    this.subscriptions.push(subscription);
  }

  ngOnDestroy(): void {
    this.subscriptions?.forEach(subscription => subscription.unsubscribe());
  }

  downloadAllDocumentsHandler(documentCategory: string) {
    let documentIds: string[] = [];
    switch (documentCategory) {
      case DOCUMENT_CATEGORY_SECTION.USER:
        this.documents
          .filter(x => x.isDocumentUploadedByUser)
          .forEach(document => {
            this.downloadDocument(document, true);
            if (!isNotEmpty(document.userFirstDownloadDate)) {
              documentIds.push(document.documentId);
            }
          });

        break;

      case DOCUMENT_CATEGORY_SECTION.LEASING_PARTNER:
        this?.documents
          .filter(x => !x.isDocumentUploadedByUser)
          .forEach(document => {
            this.downloadDocument(document, true);
            if (!isNotEmpty(document.userFirstDownloadDate)) {
              documentIds.push(document.documentId);
            }
          });
        if (documentIds.some(x => x) && this._globalService.user.roles.includes(USER_ROLES.USER)) {
          this.markDocumentsRead.next(documentIds);
        }

        break;
    }
  }

  downloadDocument(file: app.models.RequestXDocument, fromDownloadAll: boolean = false) {
    if (file.documentStoragePath) {
      this._fileStoreAPIService.getFile(file.documentStoragePath).subscribe(response => {
        if (response) {
          const documentRef = `data:application/pdf;base64,${response}`;
          const downloadLink = document.createElement('a');

          downloadLink.href = documentRef;
          downloadLink.download = file.documentName;
          downloadLink.click();

          if (
            !fromDownloadAll &&
            this._globalService.user.roles.includes(USER_ROLES.USER) &&
            !isNotEmpty(file.userFirstDownloadDate) &&
            !file.isDocumentUploadedByUser
          ) {
            this.markDocumentsRead.next([file.documentId]);
          }
        }
      });
    }
  }

  filterUserRecordsCallback = (array: any[]): any[] =>
    array.filter(x => (this.isAdmin ? !x.isDocumentUploadedByUser : x.isDocumentUploadedByUser));

  filterPartnerRecordsCallback = (array: any[]): any[] =>
    array.filter(x => (this.isAdmin ? x.isDocumentUploadedByUser : !x.isDocumentUploadedByUser));

  uploadFileInStoreHandler(event: any) {
    let file = event.target.files[0] as File;
    event.target.value = null;
    let fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (!fileExtension || !this.UPLOAD_ALLOWED_EXTENSIONS.includes(fileExtension)) {
      this._notificationService.showWarning(
        this._translateService.instant('common.uploadedFileWrongExtension'),
        this._translateService.instant('common.warning')
      );
      return;
    }

    const allowedFileSizeinBytes = this._envConfig.ALLOWED_FILE_SIZE * 1000000;
    if (file.size > allowedFileSizeinBytes) {
      this._notificationService.showWarning(
        this._translateService.instant('common.fileSizeExceeded', { maxSize: this._envConfig.ALLOWED_FILE_SIZE }),
        this._translateService.instant('common.warning')
      );
      return;
    }

    this.uploadFile(file);
  }

  private uploadFile(file: File) {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);

    this._fileStoreAPIService.postFilesWithEvents(formData).subscribe(response => {
      if (isNotEmpty(response)) {
        const failEvents = response.events.filter(x => x.eventType == FileEvents.FilePostFailed);

        if (failEvents.length > 0) {
          const eventCodes = failEvents.map(e => e.eventBody.eventCode);

          let notification = '';

          if (eventCodes.includes(FILE_UPLOAD_ERROR_CODE.VIRUS)) {
            notification =
              failEvents.length > 1 ? 'common.uploadedFilesContainsVirus' : 'common.uploadedFileContainsVirus';
          } else if (eventCodes.includes(FILE_UPLOAD_ERROR_CODE.INVALID_FILE_TYPE)) {
            notification = failEvents.length > 1 ? 'common.uploadedFilesInvalidType' : 'common.uploadedFileInvalidType';
          }
          if (notification) {
            this._notificationService.showWarning(
              this._translateService.instant(notification),
              this._translateService.instant('common.warning')
            );
          }
          return;
        }
        response.events.forEach(event => {
          const uploadedFiles = event.eventBody.uploadedFiles;
          const [key, value] = Object.entries(uploadedFiles)[0];
          const reader = new FileReader();
          reader.onload = (readerEvent: any) => {
            this.toUploadDocuments.push({
              documentName: value,
              documentSize: readerEvent.total,
              documentStoragePath: key,
            } as app.models.RequestXDocument);
          };
          reader.readAsDataURL(file);
        });
      }
    });
  }

  public removeFileHandler(index: number): void {
    const file = this.toUploadDocuments[index];
    const documentId = file.documentStoragePath;

    this._fileStoreAPIService.deleteFile(documentId).subscribe(response => {
      if (isNotEmpty(response)) {
        if (response.response.events.some(x => x.eventType == FileEvents.FileDeleted)) {
          this.toUploadDocuments.splice(index, 1);
        } else {
          this._notificationService.showError(
            this._translateService.instant('common.fileDeleteFailed'),
            this._translateService.instant('common.error')
          );
          return;
        }
      }
    });
  }

  uploadFileToRequestHandler() {
    if (!this.toUploadDocuments.some(x => x)) {
      return;
    }

    this._requestAPIService
      .linkUploadedDocumentsToRequest(this.requestId, this.requestTypeCode, this.toUploadDocuments)
      .subscribe(x => {
        if (x.isSuccess) {
          this._notificationService.showSuccess(
            this._translateService.instant('requests.clientData.notification.documentUploaded'),
            this._translateService.instant('common.success')
          );

          const eventObjects = x.response.events.find(
            x => x.eventType == RequestDocumentsEvents.RequestDocumentsLinked
          )!.eventBody.documents;

          this.toUploadDocuments
            .map(x => {
              const eventObject = eventObjects.find(y => y.documentStoragePath == x.documentStoragePath);

              return {
                ...x,
                isDocumentUploadedByUser: eventObject.isDocumentUploadedByUser,
                documentDate: eventObject.documentDate,
                documentId: eventObject.documentId,
              };
            })
            .forEach(doc => {
              this.documents.push(doc);
            });
          this.documents.sort((a, b) => {
            return new Date(b.documentDate).getTime() - new Date(a.documentDate).getTime();
          });
          this.toUploadDocuments = [];
        }
      });
  }

  openImageCaptureModal() {
    let modal = this._ngbModal.open(ImageCaptureModalComponent, { centered: true, backdrop: 'static' });

    modal.result.then(result => {
      if (result) {
        this.uploadFile(result);
      }
    });
  }
}
